import AccountStatementsAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/account-statements/account-statements.entity';
import { GridConfig } from 'app/shared/components/smart-grid/core/interfaces/grid-config.interface';
import { ColDef } from 'app/shared/components/smart-grid/core/interfaces/col-def.interface';
import { BusinessPartnerSimpleCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/business-partner-simple-cell-renderer/business-partner-simple-cell-renderer.component';
import { DocumentCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/document-cell-renderer/document-cell-renderer.component';
import { DateOnlyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/date-only-cell-renderer/date-only-cell-renderer.component';
import { ExpiredDaysCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/expired-days-cell-renderer/expired-days-cell-renderer.component';
import { PaymentConditionCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/payment-condition-cell-renderer/payment-condition-cell-renderer.component';
import { ReferenceCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/reference-cell-renderer/reference-cell-renderer.component';
import { CurrencyCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/currency-cell-renderer/currency-cell-renderer.component';
import { SimpleCellRendererComponent } from 'app/shared/components/smart-grid/cell-renderers/simple-cell-renderer/simple-cell-renderer.component';

export interface CustomerAccountSummary {
  customerName: string;
  customerCode: string;
  totalItems: number;
  totalAmount: number;
  totalBalance: number;
  totalExpired: number;
}

// Objeto nuevo por instancia para no compartir estado mutable del grid entre componentes
export function buildOpenItemsGridConfig(): GridConfig<AccountStatementsAttributes> {
  return {
  emptyState: {
    message: "No encontramos tus resultados!",
    showIcon: true,
  },
  pagination: {
    enabled: true,
    mode: "api",
    pageSize: 50,
    pageSizeOptions: [50, 100, 150, 200],
    showPaginator: true,
  },
  sorting: {
    enabled: true,
    mode: "api",
    multiColumn: false,
    defaultSort: [{ field: "totalAmount" as keyof AccountStatementsAttributes, direction: "desc" }],
  },
  search: {
    enabled: false,
  },
  columnFilters: {
    enabled: true,
    mode: "local",
  },
  columnVisibility: {
    enabled: false,
  },
  selection: {
    enabled: false,
  },
  autoSizing: {
    enabled: true,
    includeHeaders: true,
    skipHidden: true,
    padding: 40,
  },
  styling: {
    maxHeight: "85vh",
    minHeight: "80vh",
    density: "comfortable",
    fontSize: "12px",
    cellPaddingX: "3px",
    cellPaddingY: "3px",
  },
  settingsPanel: {
    enabled: true,
    position: "right",
    enableColumnVisibility: true,
    defaultDensity: "compact",
  },
  responsive: {
    enabled: true,
    breakpoints: {
      mobile: 640,
      tablet: 1024,
    },
    mobileMode: 'compact',
    autoHideColumns: true,
    hideColumnsOnMobile: [
      'creationDate' as keyof AccountStatementsAttributes,
      'availableDays' as keyof AccountStatementsAttributes,
      'paymentConditionShort' as keyof AccountStatementsAttributes,
      'additionalOC' as keyof AccountStatementsAttributes,
    ],
    hideColumnsOnTablet: [
      'paymentConditionShort',
      'additionalOC',
    ],
    criticalColumns: [
      'document',
      'expirationDate',
      'balance',
    ],
    touchOptimized: true,
    minTouchTargetSize: 44,
  },
  };
}

// Días vencidos a partir de la fecha de vencimiento (0 si no está vencida o es inválida)
export function getOpenItemExpiredDays(expirationDate: Date): number {
  if (!expirationDate) return 0;
  const today = new Date();
  const expiration = new Date(expirationDate);
  today.setHours(0, 0, 0, 0);
  expiration.setHours(0, 0, 0, 0);
  if (isNaN(expiration.getTime())) return 0;
  const dayDifference = (today.getTime() - expiration.getTime()) / (1000 * 3600 * 24);
  return dayDifference > 0 ? Math.floor(dayDifference) : 0;
}

// Columnas de la grilla; recibe el formateador de moneda porque depende de la config de decimales del componente
export function buildOpenItemsColumnDefs(
  formatCurrencyForFilter: (value: number | null | undefined) => string,
): ColDef<AccountStatementsAttributes>[] {
  return [
    {
      field: 'businessPartner' as keyof AccountStatementsAttributes,
      headerName: 'Socio de negocio',
      cellRenderer: BusinessPartnerSimpleCellRendererComponent,
      minWidth: 200,
      sortable: true,
      filterOptions: {
        valueExtractor: (item) => {
          const code = item.businessPartner?.externalKeys?.code || '';
          const name = item.businessPartner?.name || '';
          return code ? `${code} - ${name}` : name;
        },
        customFilterFn: (rowData, filterValue) => {
          const code = rowData.businessPartner?.externalKeys?.code || '';
          const name = rowData.businessPartner?.name || '';
          const displayValue = code ? `${code} - ${name}` : name;
          return displayValue === filterValue;
        },
      },
    },
    {
      colId: "document",
      headerName: "Nro. Doc.",
      cellRenderer: DocumentCellRendererComponent,
      minWidth: 150,
      sortable: true,
      valueGetter: (row) => row.document,
      filterOptions: {
        valueExtractor: (item) => item.document || "-",
        customFilterFn: (rowData, filterValue) => {
          const docNumber = rowData.document || "-";
          return docNumber === filterValue;
        },
      },
    },
    {
      field: "codeReason",
      headerName: "Tipo",
      cellRenderer: SimpleCellRendererComponent,
      minWidth: 120,
      sortable: true,
    },
    {
      field: "creationDate",
      headerName: "Fecha de creación",
      cellRenderer: DateOnlyCellRendererComponent,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "expirationDate",
      headerName: "Fecha de vencimiento",
      cellRenderer: DateOnlyCellRendererComponent,
      minWidth: 150,
      sortable: true,
    },
    {
      colId: "availableDays",
      headerName: "Días vencidos",
      cellRenderer: ExpiredDaysCellRendererComponent,
      minWidth: 120,
      sortable: true,
      valueGetter: (row) => row.expirationDate,
      cellClass: "text-right",
      headerClass: "text-right",
      filterOptions: {
        valueExtractor: (item) => String(getOpenItemExpiredDays(item.expirationDate)),
        customFilterFn: (rowData, filterValue) =>
          String(getOpenItemExpiredDays(rowData.expirationDate)) === filterValue,
      },
    },
    {
      field: "paymentConditionShort" as keyof AccountStatementsAttributes,
      colId: "paymentConditionShort",
      headerName: "Condición de pago",
      cellRenderer: PaymentConditionCellRendererComponent,
      minWidth: 180,
      sortable: true,
      filterOptions: {
        valueExtractor: (item) => {
          const code = item.paymentConditionShort?.externalKeys?.code || "";
          const name = item.paymentConditionShort?.name || "";
          return code ? `${code} - ${name}` : name;
        },
        customFilterFn: (rowData, filterValue) => {
          const code = rowData.paymentConditionShort?.externalKeys?.code || "";
          const name = rowData.paymentConditionShort?.name || "";
          const displayValue = code ? `${code} - ${name}` : name;
          return displayValue === filterValue;
        },
      },
    },
    {
      colId: "reference",
      headerName: "Referencia",
      field: "reference",
      minWidth: 200,
      sortable: true,
      valueGetter: (row) => row.reference || "-",
    },
    {
      colId: "additionalOC",
      headerName: "Referencia adicional",
      cellRenderer: ReferenceCellRendererComponent,
      minWidth: 200,
      sortable: true,
      valueGetter: (row) => row.additionalOC,
    },
    {
      colId: "invoiceReference",
      headerName: "Referencia de factura",
      field: "invoiceReference",
      minWidth: 200,
      sortable: true,
      valueGetter: (row) => row.invoiceReference || "-",
    },
    {
      field: "totalAmount",
      headerName: "Total",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 150,
      sortable: true,
      cellClass: "text-right",
      headerClass: "text-right",
      filterOptions: {
        valueExtractor: (item) => formatCurrencyForFilter(item.totalAmount),
        customFilterFn: (rowData, filterValue) =>
          formatCurrencyForFilter(rowData.totalAmount) === filterValue,
      },
    },
    {
      field: "balance",
      headerName: "Saldo",
      cellRenderer: CurrencyCellRendererComponent,
      minWidth: 150,
      sortable: true,
      cellClass: "text-right",
      headerClass: "text-right",
      filterOptions: {
        valueExtractor: (item) => formatCurrencyForFilter(item.balance),
        customFilterFn: (rowData, filterValue) =>
          formatCurrencyForFilter(rowData.balance) === filterValue,
      },
    },
  ];
}

// Resumen KPI del estado de cuenta; NC y SFC restan al saldo total
export function buildOpenItemsSummary(
  data: AccountStatementsAttributes[],
  totalItems: number,
  customer: { name: string; code: string } | null,
): CustomerAccountSummary | null {
  if (!customer || data.length === 0) return null;
  const SUBTRACT_TYPES = ['NC', 'SFC'];
  return {
    customerName: customer.name,
    customerCode: customer.code || "N/A",
    totalItems,
    totalAmount: data.reduce((sum, item) => sum + (item.totalAmount || 0), 0),
    totalBalance: data.reduce((sum, item) => {
      const balance = item.balance || 0;
      return SUBTRACT_TYPES.includes(item.documentType || '') ? sum - balance : sum + balance;
    }, 0),
    totalExpired: data.filter((item) => getOpenItemExpiredDays(item.expirationDate) > 0).length,
  };
}
